/**
 * SmartTask V2 - ChildEntryForm Component
 *
 * Full-screen panel (matches ActivePanel / SchedulePanel / RoutePanel pattern).
 * Dynamic form for child entries. Expense subtypes (fuel, toll, maintenance,
 * purchase) are captured as unified `expense` work items
 * with `item_subtype` preserved. Non-expense types (site_visit, assessment,
 * supervision, training, route_stop) remain as standalone entries.
 *
 * All expense subtypes support receipt photo capture via useCamera + Gemini Vision AI.
 */

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-finance/workflows/expenses-management/capturing-expenses.mdx
 * (published at user-docs.totalaccess.co.za/total-finance/workflows/expenses-management/capturing-expenses)
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { useCamera, CameraModal } from '@/hooks/useCamera';
import { getChildEntryFormRegistry } from '@/config/child-entry-form/configLoader';
import type { CameraConfigYaml } from '@/config/child-entry-form/types';
import { ensureCameraPermission } from '@/lib/cameraAccess';
import { authService } from '@/lib/authService';
import { useTheme } from '@/contexts/ThemeContext';
import { useNetwork } from '@/lib/networkService';
import {
  getSmartTaskViewportPageFrameClassName,
  getSmartTaskViewportPageFrameStyle,
  SMARTTASK_TOPMOST_FULLSCREEN_MODAL_Z_INDEX,
  useSmartTaskViewport,
} from '../viewport';
import type { ChildEntryType, GPSLocation, FeatureFlags } from '../types';
import { useOdometerCapture } from '../hooks/useOdometerCapture';
import {
  useExpenseAccounts,
  useProcurementSearch,
  useCustomerJobSearch,
  useVehicleSearch,
  useCaptureSession,
  useCapturePersistence,
  useReceiptAnalysis,
  useVehicleInfo,
  useCaptureModeButtons,
  useLinkableWorkItemDocs,
  useLastFuelEntry,
  type VehicleInfo,
  ReceiptCaptureSection,
  usePdfImport,
  useImageImport,
  EXPENSE_ENTRY_TYPES,
  ENTRY_CONFIG,
  createInitialFormData,
  toRecordString,
  fromRecordString,
  type CaptureFormData,
  type CaptureFieldKey,
  type CaptureCapabilities,
  FULL_TRUST_CAPABILITIES,
  resolveCapabilities,
  CHILD_ENTRY_FORM_Z_INDEX,
} from './expense';
import {
  buildReceiptAmountValidation,
  getReceiptCaptureGuide,
  type ReceiptCaptureProfile,
  type ReceiptAiPayload,
  type ReceiptAiValidation,
} from '@/lib/receiptAiAnalysis';
import { isVehicleLinkedExpenseType } from '@/app/client-zone/modules/total-operations/utils/expenseLinkage';
import { useExpensePolicies } from '@/hooks/useExpensePolicies';
import { useWorkflowActionPermissions } from '@/lib/operations/workflowActionPermissions';
import {
  hasOperationsActionPermissionRow,
  isItemTypeVisibleInSmartTask,
} from '@/lib/operations/actionPermissions';
import type { FinanceWorkflowMode, ProcurementLifecycleMode } from '@/lib/finance/workflowMode';
import { supplierNamesMatch } from '@/lib/finance/supplierNameMatch';
import type { DocumentLineItem } from '@/hooks/useDocumentsWorkItems';
import { TypeSelectionScreen } from './TypeSelectionScreen';
import { EntryFormHeader } from './EntryFormHeader';
import { FuelInsightsCard } from './FuelInsightsCard';
import {
  type LinkingMode,
  type VehicleExpenseSubType,
  ProcurementPostCapture,
} from './LinkToggleSection';
import { FormFieldsSection, type DisplayAccountCode } from './FormFieldsSection';
import { type DuplicateWarning } from './DuplicateWarningBanner';
import { BottomActionBar } from './BottomActionBar';
import { SubmissionReceipt, type SubmissionReceiptData } from './SubmissionReceipt';
import { SelectionSectionsAccordion } from './SelectionSectionsAccordion';
import { AutoSwitchBanner } from './AutoSwitchBanner';
import { OcrConfidenceIndicator } from './OcrConfidenceIndicator';
import { CaptureButtonConfigModal } from './CaptureButtonConfigModal';
import { AiAnalysisOverlay, SubmittingOverlay } from './EntryFormLoadingOverlays';
import { haptics } from '@/lib/haptics';
import { notify, confirm } from '@/lib/notifications';
import { createLogger } from '@/lib/debugLogger';
import { GuidedTour } from '@/components/training/guided-tour';

const logger = createLogger('ChildEntryForm');

// Workflow whose Actions-tab matrix rows gate the Smart Capture linking
// modes. Rows are declared as `extra_matrix_rows` in
// operational-workflows/expenses-management.yaml.
const EXPENSES_WORKFLOW_KEY = 'expenses-management';
const LINKING_MODE_MATRIX_ROWS: Record<Exclude<LinkingMode, null>, string> = {
  supplier: 'capture_supplier_document',
  job: 'capture_job_expense',
  both: 'capture_supplier_job_expense',
  general: 'capture_general_expense',
  vehicle: 'capture_vehicle_expense',
  wages: 'capture_casual_wage',
};

interface ChildEntryFormProps {
  isOpen: boolean;
  entryType?: ChildEntryType; // Optional - if not provided, show type selection screen
  workItemId: string;
  /** UUID of the currently assigned vehicle — used to fetch fuel type & live odometer */
  vehicleId?: string | null;
  vehicleRegistration?: string | null;
  gpsLocation?: GPSLocation | null;
  onSubmit: (entry: ChildEntryFormData) => void;
  onCancel: () => void;
  onTakePhoto?: () => void;
  templateColor?: string;
  /** Active work item the user may optionally link this entry to */
  linkableWorkItemId?: string | null;
  /** Label for the linkable work item (e.g. job number or title) */
  linkableWorkItemLabel?: string | null;
  /** Rich appointment details for the linkable work item */
  linkableWorkItemNumber?: string | null;
  linkableWorkItemTitle?: string | null;
  linkableWorkItemContactName?: string | null;
  linkableWorkItemLocation?: string | null;
  /** Override the default header title (derived from ENTRY_CONFIG) */
  titleOverride?: string;
  /** Explicitly set whether this is a job-linked expense (overrides entryTypeProp-based inference) */
  isJobLinkedExpenseOverride?: boolean | undefined;
  /** Finance workflow mode — when standard or strict, expenses require approval first */
  workflowMode?: FinanceWorkflowMode | null;
  /** Procurement lifecycle mode — simplified hides procurement types, standard allows flexible linking, strict requires PO */
  procurementLifecycleMode?: ProcurementLifecycleMode | null;
  /** Callback to go back to the begin action modal (when coming from workflow modal) */
  onBackToBeginAction?: () => void;
  /** Feature flags for RBAC-based filtering of entry types */
  features?: FeatureFlags | null;
  /** When true, hide the "Link to active appointment" toggle and auto-link. Used when called from AppointmentDetailPanel. */
  hideLinkToggle?: boolean;
  /** When true, hide the X close button in the header. Used in public upload mode where there's nowhere to cancel to. */
  hideCloseButton?: boolean;
  /** Capability flags controlling what the user can do in the capture form.
   *  Use `FULL_TRUST_CAPABILITIES` for finance users, `ZERO_TRUST_CAPABILITIES` for field workers.
   *  Defaults to full-trust. GL allocation is auto-disabled for job-linked expenses regardless. */
  capabilities?: CaptureCapabilities;
  /** When set, the form operates in public (unauthenticated) mode: AI analysis
   *  routes through the token-authenticated public validate-receipt endpoint,
   *  submission POSTs to the public document-upload endpoint, and auth-dependent
   *  features (expense accounts, procurement, vehicle, policies) degrade gracefully. */
  publicUploadToken?: string | null;
  /** Pre-fill form fields (e.g. from DigitalLogbook trip data for travel expenses).
   *  Keys must match the field keys defined in ENTRY_CONFIG for the selected entry type. */
  prefillData?: Record<string, string> | null;
}

// ============================================================================
type ChildEntryMetadata = Record<string, unknown>;

export interface ChildEntryFormData {
  type: ChildEntryType;
  work_item_id: string;
  timestamp: string;
  location?: GPSLocation;
  amount?: number;
  notes?: string;
  photos?: string[];
  metadata: ChildEntryMetadata;
  /** When true, the entry was already persisted as a finance document — SmartTaskShell should only update local state */
  already_persisted?: boolean;
}

// ============================================================================
// ENTRY TYPE CONFIGURATIONS — imported from ./expense/constants
// ============================================================================

function isCapacitorApp(): boolean {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function ChildEntryForm({
  isOpen,
  entryType: entryTypeProp,
  workItemId,
  vehicleId,
  vehicleRegistration,
  gpsLocation,
  onSubmit,
  onCancel,
  onTakePhoto: _onTakePhoto,
  templateColor,
  linkableWorkItemId,
  linkableWorkItemLabel,
  linkableWorkItemNumber,
  linkableWorkItemTitle,
  linkableWorkItemContactName,
  linkableWorkItemLocation,
  titleOverride,
  isJobLinkedExpenseOverride,
  workflowMode,
  procurementLifecycleMode,
  onBackToBeginAction,
  features,
  hideLinkToggle,
  hideCloseButton = false,
  capabilities = FULL_TRUST_CAPABILITIES,
  publicUploadToken,
  prefillData,
}: ChildEntryFormProps) {
  const isMobile = isCapacitorApp();
  const { isTabletPortal } = useSmartTaskViewport();
  const { currentTheme } = useTheme();
  const network = useNetwork();
  const isOnline = network.isConnected;
  const isDark = currentTheme?.mode === 'dark';
  const realtimeClientId = useMemo(() => {
    const rawClientId = authService.getSessionClientId();
    return rawClientId != null ? String(rawClientId) : null;
  }, []);
  const { policies } = useExpensePolicies();

  // ── Submission receipt state ───────────────────────────────────────────
  const [submissionReceipt, setSubmissionReceipt] = useState<SubmissionReceiptData | null>(null);

  // ── Header visibility (matches ActivePanel pattern) ──────────────────
  const [headerHidden, setHeaderHidden] = useState(false);
  useEffect(() => {
    if (!isMobile) return;
    const onHeaderChange = (e: Event) => {
      const visible = (e as CustomEvent).detail?.visible;
      setHeaderHidden(!visible);
    };
    window.addEventListener('headerVisibilityChange', onHeaderChange);
    return () => window.removeEventListener('headerVisibilityChange', onHeaderChange);
  }, [isMobile]);

  useEffect(() => {
    if (isOpen && isMobile) {
      window.dispatchEvent(new Event('showHeader'));
    }
  }, [isOpen, isMobile]);

  // ── Type selection state ───────────────────────────────────────────────
  const [selectedEntryType, setSelectedEntryType] = useState<ChildEntryType | null>(entryTypeProp || null);

  // Fleet Management "Capture Expense" locks the form to vehicle expense mode
  // (Fuel / Toll / Maintenance) tied to the assigned vehicle. The 6-button
  // selector is hidden so only the vehicle sub-type picker shows.
  const lockToVehicleExpense = prefillData?.lockToVehicleExpense === 'true';
  // ExpensesManagementModal mode tiles force a single linking mode —
  // validated against the LinkingMode enum before use.
  const forcedLinkingMode = ((): Exclude<LinkingMode, null> | null => {
    const v = prefillData?.linking_mode;
    return v === 'supplier' || v === 'job' || v === 'both'
      || v === 'general' || v === 'vehicle' || v === 'wages' ? v : null;
  })();
  const forcedVehicleSubType = ((): VehicleExpenseSubType => {
    const v = prefillData?.vehicle_sub_type;
    return v === 'fuel' || v === 'toll' || v === 'maintenance' ? v : null;
  })();

  // Track whether this form session started in Smart Capture mode (purchase type,
  // no job-linked override, not procurement). This persists even after the user
  // switches to vehicle/wages modes via the 6-button selector, so the selector
  // remains available in the accordion for mode switching.
  const smartCaptureModeRef = useRef(false);

  // Finance workflow mode awareness — when standard/strict, all expense entries
  // go to DocumentApprovalBoard first. In simplified mode they go directly to
  // FinanceDocumentView with status 'approved'.
  const requiresFinanceApproval = useMemo(() => {
    if (!workflowMode || workflowMode === 'simplified') return false;
    const type = selectedEntryType || entryTypeProp;
    if (!type) return false;
    return EXPENSE_ENTRY_TYPES.includes(type);
  }, [workflowMode, selectedEntryType, entryTypeProp]);

  // ── Capture session (headless state machine) ───────────────────────
  // Manages form data, receipt photos, AI results, multi-currency, tax resolution,
  // line items, metadata, photo upload, and expense intake API submission.
  const {
    formData,
    setFormData,
    receiptPhotos,
    setReceiptPhotos,
    receiptResult,
    setReceiptResult,
    receiptValidation,
    setReceiptValidation,
    detectedCurrency,
    setDetectedCurrency,
    baseCurrency,
    submitting,
    loadingPhase,
    submit: captureSessionSubmit,
    resetSessionId: captureSessionResetSessionId,
  } = useCaptureSession(isOpen, publicUploadToken);

  // ── Expense accounts & tax rates (via hook) ──────────────────────────
  const {
    expenseAccounts,
    expenseAccountMappings,
    taxRates,
    preferredDefaultTaxRate,
    displayAccountCode,
  } = useExpenseAccounts({ selectedEntryType, entryTypeProp, formData });

  // ── Link to active work item toggle ───────────────────────────────────
  const [linkToWorkItem, setLinkToWorkItem] = useState(false);
  const [isJobLinkedExpense, setIsJobLinkedExpense] = useState(false);

  // ── Linking mode for Smart Capture (6-button selector) ───────────────
  // null = nothing selected (hide capture), 'supplier' = supplier only,
  // 'job' = job/appointment, 'both' = supplier + job, 'general' = immediate capture,
  // 'vehicle' = vehicle expense (fuel/toll/maintenance, locked to assigned vehicle),
  // 'wages' = casual wages
  const [linkingMode, setLinkingMode] = useState<LinkingMode>(null);
  // Vehicle sub-type for the 'vehicle' linking mode
  const [vehicleSubType, setVehicleSubType] = useState<VehicleExpenseSubType>(null);

  // ── Selection sections accordion (collapses after photo capture) ────
  const [selectionExpanded, setSelectionExpanded] = useState(true);
  const prevPhotoCountForCollapse = useRef(0);
  useEffect(() => {
    if (receiptPhotos.length > 0 && prevPhotoCountForCollapse.current === 0) {
      setSelectionExpanded(false);
    }
    if (receiptPhotos.length === 0 && prevPhotoCountForCollapse.current > 0) {
      setSelectionExpanded(true);
    }
    prevPhotoCountForCollapse.current = receiptPhotos.length;
  }, [receiptPhotos.length]);

  // ── Link to my vehicle toggle (vehicle-linked expenses) ──────────────
  const [linkToVehicle, setLinkToVehicle] = useState(false);

  // ── Vehicle search hook (for dropdown when toggle is off) ─────────────
  const isVehicleExpense = isVehicleLinkedExpenseType(selectedEntryType);
  const {
    selectedVehicle: searchedVehicle,
    setSelectedVehicle: setSearchedVehicle,
    vehicleSearchQuery,
    setVehicleSearchQuery,
    showVehicleSearch,
    setShowVehicleSearch,
    loadingVehicles,
    filteredVehicles,
    vehicleDropdownRef,
    reset: resetVehicleSearch,
  } = useVehicleSearch({ isVehicleExpense });

  // The effective vehicle to use for this expense — either the assigned
  // vehicle (when toggle is on) or the user-selected vehicle from the dropdown.
  const effectiveVehicleId = linkToVehicle ? vehicleId : searchedVehicle?.id ?? null;
  const effectiveVehicleRegistration = linkToVehicle ? vehicleRegistration : searchedVehicle?.registration ?? null;

  // ── Linked POs on the linkable work item (restricts procurement toggle) ──
  const { hasLinkedPOs: linkableWorkItemHasLinkedPOs } = useLinkableWorkItemDocs({ linkableWorkItemId });

  // ── Derived entry type (needed by useVehicleInfo and useOdometerCapture) ──
  const entryType = selectedEntryType || entryTypeProp;

  // ── Vehicle info (fetched from fleet API via useVehicleInfo hook) ────
  const { vehicleInfo, setVehicleInfo } = useVehicleInfo({
    isOpen,
    entryType,
    effectiveVehicleId,
    workItemId,
    vehicleId,
    realtimeClientId,
    setFormData,
  });

  // ── Smart Odometer Capture (fuel + maintenance entries with vehicle) ──
  const needsOdometer = selectedEntryType && (selectedEntryType === 'fuel' || selectedEntryType === 'maintenance') && !!effectiveVehicleId;
  const odometerCapture = useOdometerCapture({
    vehicleId: needsOdometer ? effectiveVehicleId : null,
    vehicleName: effectiveVehicleRegistration || (vehicleInfo?.fuelType ? `Vehicle (${vehicleInfo.fuelType})` : undefined),
    autoFetch: needsOdometer ? isOpen : undefined,
    onReadingChange: (reading) => {
      // Propagate null too — clearing the manual odometer input must
      // clear the field so the required-field gate re-locks.
      setFormData(prev => ({ ...prev, odometer: reading != null ? String(reading) : '' }) as CaptureFormData);
    },
  });
  const resetOdometerCapture = odometerCapture.reset;

  // ── Last fuel entry (for fuel insights: distance, efficiency, price/litre) ──
  const isFuelEntry = selectedEntryType === 'fuel';
  const { lastFuelEntry, loading: loadingLastFuelEntry } = useLastFuelEntry({
    enabled: isFuelEntry && !!effectiveVehicleId && isOpen,
    vehicleId: effectiveVehicleId,
  });

  // ── PDF import (client-side pdf.js → base64 images) ────────────────
  const {
    importPdf,
    isImporting: isImportingPdf,
    importError: importErrorPdf,
    importProgress: importProgressPdf,
  } = usePdfImport((images) => {
    setReceiptPhotos(prev => [...prev, ...images]);
  });

  // ── Image import (file picker → downscaled base64 JPEGs) ──────────
  const {
    importImages,
    isImporting: isImportingImages,
    importError: importErrorImages,
  } = useImageImport((images) => {
    setReceiptPhotos(prev => [...prev, ...images]);
  });

  // ── Receipt AI UI state (via useReceiptAnalysis hook) ──────────────
  const [receiptPhotoProcessing, setReceiptPhotoProcessing] = useState(false);
  // Always use 'auto' capture profile — the camera UI dynamically detects
  // the document edges (receipt, A4, etc.) and draws a bounding box automatically,
  // matching the Dext experience. No manual profile selection needed.
  const captureProfile: ReceiptCaptureProfile = 'auto';
  const prevPhotoCountRef = useRef(0);
  const receiptQualityKeyRef = useRef<string | null>(null);
  const prevOpenRef = useRef(false);
  const prevEntryTypeRef = useRef(entryTypeProp);
  const submitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Entry awaiting "Done" propagation when capture-another is shown */
  const pendingEntryRef = useRef<ChildEntryFormData | null>(null);

  // Clear pending submit timeout if the form closes before the 3.5s delay
  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
        submitTimeoutRef.current = null;
      }
    };
  }, []);

  const captureGuide = useMemo(() => getReceiptCaptureGuide(captureProfile), [captureProfile]);

  // ── Camera hook (useCamera from FullVehicleInspection pattern) ───────
  const camera = useCamera({
    initialFacingMode: 'environment',
    // Torch is controlled by CameraModal's 3-state torchMode (off/on/auto),
    // not enableTorchByDefault — in a WebView, torch requires a user gesture.
    preferNativeCameraOnCapacitor: false,
    quality: 0.85,
    // Don't pass preferredWidth/preferredHeight — useCamera defaults to
    // 1280x720 for the live stream (wide FOV). High-res capture guide
    // dimensions caused phones to select a narrow-FOV lens, requiring the
    // user to hold the device ~1m away instead of ~50cm.
    compressOutput: { maxDim: 1280, quality: 0.80 },
    reviewCapture: true,
    onCapture: async (base64: string) => {
      setReceiptPhotoProcessing(true);
      try {
        setReceiptPhotos(prev => [...prev, base64]);
      } finally {
        setReceiptPhotoProcessing(false);
      }
      // AI analysis deferred — runs when all pages captured or on single capture
      // For single page: analyze immediately; for multi-page: user can trigger re-analysis
    },
  });

  // ── Camera permission preflight wrapper ───────────────────────────────
  // Ensures the native browser/OS camera permission popup is presented when
  // the user taps "Take Photo" (or retakes a poor-quality receipt). When the
  // permission was previously denied, opens native app settings on Capacitor
  // so the user can re-enable the camera — matching the SmartClock hero flow.
  const handleOpenCamera = useCallback(async () => {
    const permission = await ensureCameraPermission({
      notify: (message) => notify.error(message),
    });
    if (!permission.granted) {
      return;
    }
    void haptics.modalOpen();
    camera.open();
  }, [camera]);

  // ── Derived config ───────────────────────────────────────────────────
  const config = entryType ? ENTRY_CONFIG[entryType] : null;
  const isExpenseType = entryType ? EXPENSE_ENTRY_TYPES.includes(entryType) : false;
  // Travel/mileage is an expense type but doesn't require a receipt photo —
  // the logbook trip data (distance, odometer, route) is the evidence.
  const requiresReceiptPhoto = isExpenseType && entryType !== 'travel';
  const isProcurementType = entryType === 'delivery_note' || entryType === 'supplier_invoice' || entryType === 'supplier_quote' || entryType === 'grn' || entryType === 'purchase_return' || entryType === 'supplier_credit';

  // ── Camera config (from child-entry-configs.yaml) ───────────────────
  // Resolved per entry type: merges camera_defaults with per-type overrides.
  // Falls back to hardcoded defaults if the registry isn't initialized.
  const cameraConfig: CameraConfigYaml = useMemo(() => {
    const registry = getChildEntryFormRegistry();
    if (registry.isInitialized() && entryType) {
      const resolved = registry.getCameraConfig(entryType);
      if (resolved) return resolved;
    }
    // Fallback: match previous hardcoded behavior
    if (entryType === 'casual_wage') {
      return {
        document_profile: 'auto',
        auto_capture: false,
        auto_zoom: false,
        batch_capture: false,
        review_buttons: 'doneOnly',
        perspective_correction: false,
        dark_mode_enhance: true,
        show_context_photos: false,
        require_readable: false,
        camera_title: 'Authorization Photo',
        guide_title: 'Proof of authorization',
        guide_subtitle: 'Capture the WhatsApp message, signed slip, or other proof.',
      };
    }
    return {
      document_profile: 'till-slip',
      auto_capture: true,
      auto_zoom: true,
      require_readable: true,
      // Use the batch-aware review flow (captureAnotherDone):
      // - Fuel/toll (batch_capture=false): single-page known → Cancel + Use Photo
      // - Long till slips (batch_capture=true): multi-page possible → Cancel + Add Another + Use Photo
      // When edge detection detects all 4 corners and the profile is till-slip,
      // multiPagePossible is false, so only Cancel + Use Photo are shown.
      batch_capture: entryType === 'fuel' || entryType === 'toll' ? false : true,
      review_buttons: 'captureAnotherDone',
      perspective_correction: true,
      dark_mode_enhance: true,
      show_context_photos: true,
      camera_title: 'Receipt Photo',
      guide_title: 'Position receipt in frame',
      guide_subtitle: 'Auto-detects edges and captures when stable',
    };
  }, [entryType]);

  // ── Procurement lifecycle enforcement ─────────────────────────────────
  // simplified: hide procurement entry types entirely
  // standard: allow procurement types without requiring a PO (flexible linking)
  // strict: require a PO for supplier invoices and delivery notes
  const procurementLifecycle = procurementLifecycleMode || 'standard';
  const isProcurementSimplified = procurementLifecycle === 'simplified';
  const isProcurementStrict = procurementLifecycle === 'strict';
  const procurementHiddenTypes = useMemo<ChildEntryType[]>(
    () => (isProcurementSimplified ? ['delivery_note', 'supplier_invoice', 'grn', 'purchase_return', 'supplier_credit'] : []),
    [isProcurementSimplified],
  );
  const requiresPurchaseOrder = isProcurementType && (isProcurementStrict || workflowMode === 'strict');

  const canLinkToWorkItem = Boolean(linkableWorkItemId) && (!isProcurementType || isProcurementStrict === false || linkableWorkItemHasLinkedPOs);
  // Smart Capture linking mode: purchase type opened without job-linked override and not procurement.
  // Uses isJobLinkedExpenseOverride (initial prop) NOT isJobLinkedExpense (runtime state) because
  // the linking mode useEffect sets isJobLinkedExpense=true for 'job'/'both' modes.
  // Hidden when hideLinkToggle is true (e.g. SmartAutomation can't link to a job).
  // Persists via smartCaptureModeRef even after the user switches to vehicle/wages modes
  // via the 6-button selector, so the selector remains available for mode switching.
  const isInitialSmartCapture = !hideLinkToggle && selectedEntryType === 'purchase' && isJobLinkedExpenseOverride !== true && !isProcurementType;
  if (isInitialSmartCapture) {
    smartCaptureModeRef.current = true;
  }
  const isSmartCaptureLinkingMode = smartCaptureModeRef.current;

  // ── RBAC gate for the Smart Capture linking-mode selector ─────────────
  // Each of the 6 modes maps to an expenses-management Actions-tab matrix
  // row; its smart_task cell controls whether the mode button appears.
  // A row that is absent from the user's resolved matrix (group not yet
  // reseeded, or the user isn't governed by the expenses workflow) means
  // "not configured" — allow it, so existing users keep the full selector
  // until an admin explicitly disables a mode and saves the matrix.
  const { settings: linkingModePermSettings, role: linkingModePermRole } = useWorkflowActionPermissions();
  const allowedLinkingModes = useMemo<ReadonlySet<Exclude<LinkingMode, null>>>(() => {
    const allowed = new Set<Exclude<LinkingMode, null>>();
    for (const [mode, rowKey] of Object.entries(LINKING_MODE_MATRIX_ROWS) as [Exclude<LinkingMode, null>, string][]) {
      const oap = linkingModePermSettings?.operations_action_permissions;
      if (!hasOperationsActionPermissionRow(oap, rowKey, EXPENSES_WORKFLOW_KEY)
        || isItemTypeVisibleInSmartTask(linkingModePermSettings, linkingModePermRole, rowKey, EXPENSES_WORKFLOW_KEY)) {
        allowed.add(mode);
      }
    }
    return allowed;
  }, [linkingModePermSettings, linkingModePermRole]);

  // If the currently selected mode is no longer permitted, clear it.
  useEffect(() => {
    if (linkingMode && !allowedLinkingModes.has(linkingMode)) {
      setLinkingMode(null);
    }
  }, [linkingMode, allowedLinkingModes]);

  const showLinkToggle = !hideLinkToggle && (Boolean(linkableWorkItemId) || isJobLinkedExpense || isProcurementType) && selectedEntryType && !isVehicleLinkedExpenseType(selectedEntryType)
    && (!isSmartCaptureLinkingMode || linkingMode === 'job' || linkingMode === 'both' || linkingMode === 'wages');
  // Show vehicle link toggle for vehicle-linked types, but NOT when in Smart Capture
  // 'vehicle' mode (vehicle is auto-locked, no toggle/search needed)
  const showVehicleLinkToggle = isVehicleLinkedExpenseType(selectedEntryType) && selectedEntryType !== null
    && !(isSmartCaptureLinkingMode && linkingMode === 'vehicle');
  const isCasualWage = selectedEntryType === 'casual_wage';
  const accent = config?.accentColor || templateColor || '#3b82f6';

  // ── Effective capabilities (combines base with runtime context) ──
  const effectiveCapabilities = useMemo(
    () => resolveCapabilities(capabilities, isJobLinkedExpense),
    [capabilities, isJobLinkedExpense],
  );

  // ── Capture button config (SmartAutomation configurable slots / camera controls) ──
  const {
    configSlotIndex,
    setConfigSlotIndex,
    CONFIGURABLE_ENTRY_TYPES,
    handleConfigSelect,
    captureModeButtons,
  } = useCaptureModeButtons({
    effectiveCapabilities,
    isProcurementSimplified,
    procurementHiddenTypes,
    setSelectedEntryType,
  });

  // ── Supplier & Purchase Order state (via hook) ──────────────────────
  const {
    allSuppliers,
    loadingSuppliers,
    selectedSupplier,
    setSelectedSupplier,
    supplierSearchQuery,
    setSupplierSearchQuery,
    showSupplierSearch,
    setShowSupplierSearch,
    filteredSuppliers,
    selectedPurchaseOrder,
    setSelectedPurchaseOrder,
    poLineItems,
    poSearchQuery,
    setPoSearchQuery,
    poSearchResults,
    isSearchingPO,
    showPOSearch,
    setShowPOSearch,
    procurementSetupExpanded,
    setProcurementSetupExpanded,
    linkToSupplier,
    setLinkToSupplier,
    isPurchaseWithSupplier,
    poFulfillment,
    supplierDropdownRef,
    poDropdownRef,
    reset: resetProcurement,
  } = useProcurementSearch({
    selectedEntryType,
    isProcurementType,
    requiresPurchaseOrder,
    isOpen,
  });

  // ── Expense line items state (all expense types) ──────────────────────
  const [expenseLineItems, setExpenseLineItems] = useState<DocumentLineItem[]>([]);
  const [showAddLineItem, setShowAddLineItem] = useState(false);
  const [newLineItem, setNewLineItem] = useState({ description: '', quantity: 1, unit_price: 0, tax_rate_id: undefined as number | undefined });

  // Ref to prevent clearing receipt results when AI auto-switches entry type.
  // Declared early so it's available in the supplier-context effect below.
  const autoSwitchingRef = useRef(false);

  // ── Supplier context enrichment (§3.2.3) ──────────────────────────────
  // When a supplier is selected, fetch recent documents, outstanding balance,
  // payment terms, and credit limit to display contextual info.
  const [supplierContext, setSupplierContext] = useState<{
    recentDocuments: Array<{ id: string; item_number: string; grand_total: number; status: string; issued_at: string | null }>;
    outstandingBalance: number | null;
    paymentTermsDays: number | null;
    creditLimit: number | null;
    currentBalance: number | null;
    defaultGlAccountCode?: string;
    defaultTaxRateId?: number;
  } | null>(null);
  const [loadingSupplierContext, setLoadingSupplierContext] = useState(false);
  // Tracks when the entry type was auto-switched from 'purchase' to
  // 'supplier_invoice' because the selected supplier has payment terms > 0.
  // The user can revert this via the notice banner.
  const [paymentTermsSwitchFrom, setPaymentTermsSwitchFrom] = useState<ChildEntryType | null>(null);
  // True after the user explicitly reverts the payment-terms auto-switch —
  // sent to intake so the server keeps the cash 'expense' subtype instead of
  // re-upgrading on the supplier's terms.
  const [paymentTermsReverted, setPaymentTermsReverted] = useState(false);

  useEffect(() => {
    if (!selectedSupplier?.id) {
      setSupplierContext(null);
      setPaymentTermsSwitchFrom(null);
      setPaymentTermsReverted(false);
      return;
    }
    let cancelled = false;
    setLoadingSupplierContext(true);
    (async () => {
      try {
        const token = authService.getTokens()?.access_token;
        const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
        // Fetch recent documents for this supplier
        const docsRes = await fetch(`/api/v1/work-items?contact_id=${selectedSupplier.id}&limit=5&order=created_at:desc`, { headers });
        const docsData = docsRes.ok ? await docsRes.json() : null;
        const recentDocs = Array.isArray(docsData?.data) ? docsData.data.slice(0, 5).map((d: Record<string, unknown>) => ({
          id: String(d.id || ''),
          item_number: String(d.item_number || d.doc_number || ''),
          grand_total: Number(d.grand_total || 0),
          status: String(d.status || ''),
          issued_at: d.issued_at ? String(d.issued_at) : null,
        })) : [];
        // Fetch contact details for balance, payment terms, credit limit
        const contactRes = await fetch(`/api/v1/contacts/${selectedSupplier.id}`, { headers });
        const contactData = contactRes.ok ? await contactRes.json() : null;
        const contact = contactData?.data || contactData;
        // §3.2.3 — Fetch supplier-specific intake rules (default GL, payment terms)
        const rulesRes = await fetch('/api/v1/finance/supplier-rules', { headers });
        const rulesData = rulesRes.ok ? await rulesRes.json() : null;
        const allRules = Array.isArray(rulesData?.data) ? rulesData.data : (Array.isArray(rulesData) ? rulesData : []);
        const supplierRule = allRules.find((r: Record<string, unknown>) => Number(r.contact_id) === Number(selectedSupplier.id));
        const paymentTermsDays = contact ? Number(contact.payment_terms_days || 0) || null : null;
        if (!cancelled) {
          setSupplierContext({
            recentDocuments: recentDocs,
            outstandingBalance: contact ? Number(contact.current_balance || 0) : null,
            paymentTermsDays,
            creditLimit: contact ? Number(contact.credit_limit || 0) || null : null,
            currentBalance: contact ? Number(contact.current_balance || 0) : null,
            defaultGlAccountCode: supplierRule?.rules?.default_gl_account_code as string | undefined,
            defaultTaxRateId: supplierRule?.rules?.default_tax_rate_id as number | undefined,
          });
        }
        // §3.2.3 — Auto-populate due date from payment terms (supplier invoice only)
        if (paymentTermsDays && paymentTermsDays > 0 && selectedEntryType === 'supplier_invoice') {
          const today = new Date();
          const due = new Date(today);
          due.setDate(due.getDate() + paymentTermsDays);
          const dueStr = due.toISOString().split('T')[0];
          setFormData((prev) => ({ ...prev, due_date: dueStr }) as CaptureFormData);
        }
        // Auto-switch: if the supplier has payment terms > 0 and the user
        // selected a generic 'purchase' expense, upgrade to 'supplier_invoice'
        // because the supplier will be paid on terms (not immediate cash).
        // The user can revert via the payment-terms switch notice.
        // Never fires in General Expense mode (linkingMode === 'general') —
        // general is the fallback category and must not be re-categorized.
        if (paymentTermsDays && paymentTermsDays > 0 && selectedEntryType === 'purchase' && linkingMode !== 'general' && !autoSwitchingRef.current) {
          setPaymentTermsSwitchFrom('purchase');
          setPaymentTermsReverted(false);
          autoSwitchingRef.current = true;
          setSelectedEntryType('supplier_invoice');
          setFormData((prev) => ({ ...prev, due_date: new Date(Date.now() + paymentTermsDays * 86400000).toISOString().split('T')[0] }) as CaptureFormData);
        }
        // §3.2.3 — Auto-populate default GL from supplier rules
        if (supplierRule?.rules?.default_gl_account_code) {
          setFormData((prev) => ({ ...prev, category: supplierRule.rules.default_gl_account_code }) as CaptureFormData);
        }
      } catch {
        if (!cancelled) setSupplierContext(null);
      } finally {
        if (!cancelled) setLoadingSupplierContext(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSupplier?.id, selectedEntryType, setFormData, linkingMode]);

  // ── Draft persistence (crash recovery + resume-after-auth) ───────────
  const {
    clearDraft,
    saveDraft,
  } = useCapturePersistence({
    isOpen,
    formData,
    receiptPhotos,
    detectedCurrency,
    linkToWorkItem,
    isJobLinkedExpense,
    linkToVehicle,
    expenseLineItems,
  });

  // ── Receipt AI analysis (via useReceiptAnalysis hook) ────────────────
  const {
    runReceiptAnalysis,
    receiptValidating,
    setReceiptValidating,
    receiptError,
    setReceiptError,
    receiptCategoryWarning,
    setReceiptCategoryWarning,
    receiptCategorySeverity,
    setReceiptCategorySeverity,
    aiSupplierMatch,
    setAiSupplierMatch,
    aiSuggestingSupplier,
    setAiSuggestingSupplier,
    duplicateWarning,
    setDuplicateWarning,
    duplicateChecking,
    setDuplicateChecking,
    lineItemDuplicates,
    removeDuplicateLineItems,
    dismissLineItemDuplicates,
    autoSwitchBanner,
    dismissAutoSwitchBanner,
    aiCategoryDisagreement,
    setAiCategoryDisagreement,
  } = useReceiptAnalysis({
    formData,
    setFormData,
    setReceiptResult,
    setReceiptValidation,
    detectedCurrency,
    setExpenseLineItems,
    selectedEntryType,
    isProcurementType,
    selectedSupplier,
    setSelectedSupplier,
    selectedPurchaseOrder,
    poLineItems,
    allSuppliers,
    isOnline,
    preferredDefaultTaxRate: preferredDefaultTaxRate ?? null,
    effectiveVehicleRegistration,
    linkableWorkItemLocation,
    captureProfile,
    configFields: config?.fields ?? [],
    canSwitchEntryTypeInCamera: effectiveCapabilities.canSwitchEntryTypeInCamera,
    // A user selection is authoritative — any of the 6 linking modes
    // (supplier / job / both / general / vehicle / wages, whether forced via
    // prefillData.linking_mode or picked in the selector), the Fleet
    // Management vehicle lock, or a job-linked expense launch. The AI fills
    // fields only; disagreements are flagged on the DIN for DocumentModal
    // review, never applied and never blocking.
    suppressAutoCategorize: linkingMode !== null || lockToVehicleExpense || isJobLinkedExpenseOverride === true,
    onAutoSwitchEntryType: (newType) => {
      autoSwitchingRef.current = true;
      setSelectedEntryType(newType);
      // Auto-link assigned vehicle when AI detects a vehicle expense type
      if (['fuel', 'toll', 'maintenance'].includes(newType) && vehicleId) {
        setLinkToVehicle(true);
      }
    },
    publicUploadToken,
  });

  // ── Duplicate acknowledgment state ───────────────────────────────────
  // When duplicates are detected, the user must explicitly acknowledge
  // before the submit button becomes enabled.
  const [duplicateAcknowledged, setDuplicateAcknowledged] = useState(false);
  const hasUnacknowledgedDuplicates = !!(duplicateWarning && duplicateWarning.length > 0 && !duplicateAcknowledged);

  // ── Supplier doc-type determination state ────────────────────────────
  // When the linking mode is 'supplier' or 'both' and the AI can't determine
  // the document type (invoice / quote / delivery note), the user must pick
  // one before submit is enabled.
  const [docTypeUserPicked, setDocTypeUserPicked] = useState(false);
  const isSupplierLinkedMode = linkingMode === 'supplier' || linkingMode === 'both';
  const aiDocType = receiptResult?.document_type ?? null;
  const aiDocTypeIsSupplierDoc =
    aiDocType === 'supplier_invoice' || aiDocType === 'invoice' || aiDocType === 'tax_invoice' ||
    aiDocType === 'delivery_note' || aiDocType === 'quote' || aiDocType === 'supplier_quote' ||
    aiDocType === 'quotation' || aiDocType === 'estimate' || aiDocType === 'proposal';
  // Only block if: supplier-linked mode, AI has run (receiptResult exists),
  // AI did NOT detect a supplier doc type, and user hasn't manually picked.
  const pendingDocTypeDetermination = !!(
    isSupplierLinkedMode &&
    receiptResult &&
    !aiDocTypeIsSupplierDoc &&
    !docTypeUserPicked &&
    !isCasualWage
  );

  // ── Supplier name mismatch state ────────────────────────────────────
  // When the AI-detected vendor name doesn't match the technician's selected
  // supplier, the user must acknowledge the mismatch before submit.
  const [supplierMismatchAcknowledged, setSupplierMismatchAcknowledged] = useState(false);
  const selectedSupplierName = selectedSupplier?.company_name || selectedSupplier?.name || '';
  const aiVendorName = receiptResult?.vendor_name ?? '';
  const supplierMismatch = !!(
    isSupplierLinkedMode &&
    selectedSupplierName &&
    aiVendorName &&
    !supplierNamesMatch(selectedSupplierName, aiVendorName) &&
    !supplierMismatchAcknowledged
  );
  const hasUnacknowledgedSupplierMismatch = supplierMismatch;

  // ── Customer & Active Job search state (via hook) ───────────────────
  const {
    allCustomers,
    selectedCustomer,
    setSelectedCustomer,
    customerSearchQuery,
    setCustomerSearchQuery,
    showCustomerSearch,
    setShowCustomerSearch,
    loadingCustomers,
    filteredCustomers,
    selectedJob,
    setSelectedJob,
    jobSearchQuery,
    setJobSearchQuery,
    showJobSearch,
    setShowJobSearch,
    loadingJobs,
    jobsChecked,
    filteredJobs,
    activeJobs,
    customerDropdownRef,
    jobDropdownRef,
    reset: resetCustomerJob,
  } = useCustomerJobSearch({ isJobLinkedExpense });

  // ── Form visibility gate ─────────────────────────────────────────────
  // Capture-first: form is visible immediately for all expense types.
  // Supplier/PO selection happens after AI extraction, not before.
  // - Job-linked expenses still require a job/appointment selection
  // - Vehicle-linked expenses require a vehicle to be selected (either via
  //   the "link to my vehicle" toggle or the vehicle search dropdown)
  // - Procurement types not linked to an appointment require supplier (always)
  //   and PO (if requiresPurchaseOrder) to be selected before capture
  const isVehicleReady = !isVehicleLinkedExpenseType(selectedEntryType) || !!effectiveVehicleId;
  // Zero-trust users bypass procurement prerequisites — they capture as general expense
  // and reclassification/supplier/PO linking happens in the back-office (DocumentModal)
  // PO is NOT required at capture time — admin assigns POs in SupplierStatementsWorkspace.
  const procurementPrerequisitesMet = !effectiveCapabilities.canSwitchEntryTypeInCamera
    || !isProcurementType
    || linkToWorkItem
    || !!selectedSupplier;

  // ── Linking mode gate for Smart Capture ──
  // When linkingMode is null, capture is hidden. Each mode has its own prerequisites
  // before the capture interface becomes visible.
  // 'vehicle' requires a sub-type (fuel/toll/maintenance) to be selected.
  // 'wages' is immediately ready (casual_wage entry type).
  const linkingModePrerequisitesMet =
    !isSmartCaptureLinkingMode ||
    linkingMode === null ? true : // null is handled by the gate below
    linkingMode === 'general' ? true :
    linkingMode === 'supplier' ? !!selectedSupplier :
    linkingMode === 'job' ? (linkToWorkItem || (!!selectedCustomer && !!selectedJob)) :
    linkingMode === 'both' ? (!!selectedSupplier && (linkToWorkItem || (!!selectedCustomer && !!selectedJob))) :
    linkingMode === 'vehicle' ? !!vehicleSubType && !!effectiveVehicleId :
    linkingMode === 'wages' ? true :
    true;
  const linkingModeGate = !isSmartCaptureLinkingMode
    || (linkingMode !== null && allowedLinkingModes.has(linkingMode) && linkingModePrerequisitesMet);

  const isFormVisible = !(isJobLinkedExpense && !linkToWorkItem && !selectedJob) && isVehicleReady && procurementPrerequisitesMet && linkingModeGate;

  // ── Handle linking mode changes for Smart Capture ───────────────────
  // When the user selects a linking mode, set the appropriate flags.
  // 'job' and 'both' set isJobLinkedExpense=true so the customer/job dropdowns appear.
  // 'supplier' and 'general' keep isJobLinkedExpense=false.
  // 'vehicle' switches to a vehicle-linked expense type (fuel/toll/maintenance) and
  //   locks the expense to the assigned vehicle (linkToVehicle=true, no search).
  // 'wages' switches to the casual_wage entry type.
  // Extracted as a function so the open-reset can apply forced modes directly —
  // when a workflow tile forces the mode via prefillData, linkingMode keeps the
  // same value across re-opens and the dep-change effect below never re-fires,
  // leaving stale job-linked flags on supplier/general/vehicle modes.
  const applyLinkingMode = (mode: LinkingMode) => {
    const isJobMode = mode === 'job' || mode === 'both';
    const isSupplierMode = mode === 'supplier' || mode === 'both';
    const isVehicleMode = mode === 'vehicle';
    const isWagesMode = mode === 'wages';

    // Switch entry type for vehicle and wages modes
    if (isVehicleMode) {
      // Don't switch yet — wait for sub-type selection (fuel/toll/maintenance)
      // But lock the vehicle immediately. Keep accordion expanded so the
      // Fuel/Toll/Maintenance sub-selector is visible.
      setLinkToVehicle(true);
      setIsJobLinkedExpense(false);
      setLinkToSupplier(false);
      setLinkToWorkItem(false);
      setSelectedCustomer(null);
      setSelectedJob(null);
      setSelectedSupplier(null);
      setSupplierSearchQuery('');
      setShowSupplierSearch(false);
      return;
    }

    if (isWagesMode) {
      // Switch to casual_wage entry type.
      // Casual wages must be job-linked (like purchase) — the submit
      // validation requires a job or appointment. Keep the job-link UI
      // enabled so the user can select a customer + job or link to the
      // active appointment.
      autoSwitchingRef.current = true;
      setSelectedEntryType('casual_wage');
      setIsJobLinkedExpense(true);
      setLinkToSupplier(false);
      setLinkToWorkItem(canLinkToWorkItem);
      // Don't clear selectedCustomer/selectedJob — let the user pick.
      setSelectedSupplier(null);
      setSupplierSearchQuery('');
      setShowSupplierSearch(false);
      // Keep the selection panel expanded so the user sees the
      // customer + job search UI.
      setSelectionExpanded(true);
      return;
    }

    // For supplier/job/both/general modes, restore purchase type if we were in vehicle/wages
    if (selectedEntryType !== 'purchase') {
      autoSwitchingRef.current = true;
      setSelectedEntryType('purchase');
    }

    setIsJobLinkedExpense(isJobMode);
    setLinkToSupplier(isSupplierMode);
    if (!isJobMode) {
      setLinkToWorkItem(false);
      setSelectedCustomer(null);
      setSelectedJob(null);
    }
    if (!isSupplierMode) {
      setSelectedSupplier(null);
      setSupplierSearchQuery('');
      setShowSupplierSearch(false);
    }
    // Only collapse for 'general' — supplier/job/both need the accordion
    // expanded so the secondary selection (supplier dropdown, job picker) is visible.
    if (mode === 'general') {
      setSelectionExpanded(false);
    }
  };

  useEffect(() => {
    if (!isSmartCaptureLinkingMode) return;
    applyLinkingMode(linkingMode);
  }, [linkingMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handle vehicle sub-type selection ────────────────────────────────
  // When the user picks Fuel/Toll/Maintenance in the vehicle sub-selector,
  // switch the entry type, lock the vehicle, and collapse the accordion.
  useEffect(() => {
    if (!isSmartCaptureLinkingMode || linkingMode !== 'vehicle' || !vehicleSubType) return;
    autoSwitchingRef.current = true;
    setSelectedEntryType(vehicleSubType);
    // Vehicle is already locked (linkToVehicle=true set in the linkingMode effect)
    // Now collapse — user is done selecting
    setSelectionExpanded(false);
  }, [linkingMode, vehicleSubType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear previous receipt analysis when user manually switches entry types
  // Skip clearing when the switch is triggered by AI auto-detection (autoSwitchingRef)
  useEffect(() => {
    if (!selectedEntryType) return;
    if (autoSwitchingRef.current) {
      autoSwitchingRef.current = false;
      return;
    }
    setReceiptResult(null);
    setReceiptValidation(null);
    setReceiptError(null);
    setReceiptCategoryWarning(null);
    setReceiptCategorySeverity(null);
    setAiCategoryDisagreement(null);
    setPaymentTermsSwitchFrom(null);
    setPaymentTermsReverted(false);
    dismissAutoSwitchBanner();
  }, [selectedEntryType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset form when re-opened or entry type changes
  // Guard: only run full reset when the form transitions from closed→open
  // or when entryTypeProp actually changes while open. This prevents
  // clearing expenseLineItems when other deps (isJobLinkedExpenseOverride,
  // linkableWorkItemId) change mid-session.
  useEffect(() => {
    const justOpened = isOpen && !prevOpenRef.current;
    const entryTypeChanged = isOpen && prevOpenRef.current && prevEntryTypeRef.current !== entryTypeProp;

    if (justOpened || entryTypeChanged) {
      // Reset the idempotency session key so a fresh form open creates a
      // new DIN instead of returning the previous submission's cached
      // result. The hook's sessionIdRef persists across open/close cycles
      // because the component stays mounted (returns null when closed).
      captureSessionResetSessionId();
      // Fleet Management "Capture Expense" locks the form to vehicle expense
      // mode. Set entry type to 'purchase' so the TypeSelectionScreen is
      // bypassed and the Smart Capture linking mode selector renders with
      // only the Vehicle Expense button visible.
      const initialEntryType = lockToVehicleExpense ? 'purchase' : (entryTypeProp || null);
      setSelectedEntryType(initialEntryType);
      const initialType = entryTypeProp || 'purchase';
      const baseFormData = createInitialFormData(initialType);
      // Merge pre-fill data (e.g. from DigitalLogbook trip summary for travel expenses)
      if (prefillData && Object.keys(prefillData).length > 0) {
        setFormData(fromRecordString(initialType, prefillData));
      } else {
        setFormData(baseFormData);
      }
      setReceiptPhotos([]);
      setReceiptPhotoProcessing(false);
      setReceiptResult(null);
      setReceiptValidation(null);
      setReceiptError(null);
      setReceiptCategoryWarning(null);
      setReceiptCategorySeverity(null);
      setAiCategoryDisagreement(null);
      setReceiptValidating(false);
      const initialIsJobLinked = isJobLinkedExpenseOverride !== undefined ? isJobLinkedExpenseOverride : (entryTypeProp === 'purchase' || entryTypeProp === 'casual_wage');
      setLinkToWorkItem(hideLinkToggle || (initialIsJobLinked && Boolean(linkableWorkItemId)));
      setIsJobLinkedExpense(initialIsJobLinked);
      setVehicleInfo(null);
      resetOdometerCapture();
      prevPhotoCountRef.current = 0;
      resetProcurement();
      resetCustomerJob();
      resetVehicleSearch();
      // Default linkToVehicle to true if user has an assigned vehicle
      setLinkToVehicle(Boolean(vehicleId));
      setExpenseLineItems([]);
      setShowAddLineItem(false);
      setNewLineItem({ description: '', quantity: 1, unit_price: 0, tax_rate_id: undefined });
      setDuplicateWarning(null);
      setDuplicateChecking(false);
      setDuplicateAcknowledged(false);
      setDocTypeUserPicked(false);
      setSupplierMismatchAcknowledged(false);
      // Fleet Management "Capture Expense" locks the form to vehicle expense
      // mode (Fuel / Toll / Maintenance) tied to the assigned vehicle. The
      // 6-button selector is hidden so only the vehicle sub-type picker shows.
      if (lockToVehicleExpense) {
        setLinkingMode('vehicle');
        smartCaptureModeRef.current = true;
        applyLinkingMode('vehicle');
      } else if (forcedLinkingMode) {
        // ExpensesManagementModal mode tiles preselect the linking mode —
        // the 6-button selector is hidden and only that mode's pickers show.
        // applyLinkingMode is called directly because linkingMode keeps the
        // same value across re-opens, so the [linkingMode] effect never
        // re-fires to sync isJobLinkedExpense / linkToWorkItem / linkToSupplier.
        setLinkingMode(forcedLinkingMode);
        smartCaptureModeRef.current = true;
        applyLinkingMode(forcedLinkingMode);
      } else {
        setLinkingMode(null);
        smartCaptureModeRef.current = false;
      }
      setVehicleSubType(forcedVehicleSubType);
      // A forced vehicle sub-type must re-apply its entry type here too —
      // when the same tile re-opens, vehicleSubType keeps its value so the
      // [linkingMode, vehicleSubType] effect never re-fires.
      if ((lockToVehicleExpense || forcedLinkingMode === 'vehicle') && forcedVehicleSubType) {
        autoSwitchingRef.current = true;
        setSelectedEntryType(forcedVehicleSubType);
      }
    }

    prevOpenRef.current = isOpen;
    prevEntryTypeRef.current = entryTypeProp;
  }, [entryTypeProp, isOpen, isJobLinkedExpenseOverride, resetOdometerCapture, linkableWorkItemId, resetProcurement, resetCustomerJob, resetVehicleSearch, hideLinkToggle, vehicleId, prefillData, captureSessionResetSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vehicle info fetching + realtime refresh now handled by useVehicleInfo hook.

  // Auto-populate line items from remaining PO quantities (delivery note only)
  useEffect(() => {
    if ((selectedEntryType === 'delivery_note' || selectedEntryType === 'grn') && poFulfillment && !poFulfillment.isFullyDelivered) {
      const remainingItems: DocumentLineItem[] = poFulfillment.lineItems
        .filter((line) => line.remainingQty > 0)
        .map((line, idx) => ({
          description: line.description,
          quantity: line.remainingQty,
          unit_price: line.unit_price || 0,
          line_type: 'item' as const,
          sort_order: idx + 1,
          unit: 'each',
          tax_rate: 0,
          tax_amount: 0,
        }));
      if (remainingItems.length > 0 && expenseLineItems.length === 0) {
        setExpenseLineItems(remainingItems);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntryType, poFulfillment]);

  const updateField = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }) as CaptureFormData);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // runReceiptAnalysis is provided by useReceiptAnalysis hook above.

  // Auto-analyze when first photo is captured (single-shot mode)
  // For multi-page, user adds more then analysis auto-runs or they re-trigger.
  // Casual wages skip AI analysis — the photo is proof of authorization, not a receipt.
  // Travel/mileage skips AI analysis — the logbook trip data is the evidence, not a receipt.
  useEffect(() => {
    if (selectedEntryType === 'casual_wage' || selectedEntryType === 'travel') return;
    if (receiptPhotos.length > 0 && prevPhotoCountRef.current === 0) {
      // First photo captured — run analysis immediately
      runReceiptAnalysis(receiptPhotos);
    } else if (receiptPhotos.length > prevPhotoCountRef.current && prevPhotoCountRef.current > 0) {
      // Additional page added — re-run analysis with all pages
      runReceiptAnalysis(receiptPhotos);
    }
    prevPhotoCountRef.current = receiptPhotos.length;
  }, [receiptPhotos.length, selectedEntryType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!receiptResult) return;
    const formDataRecord = toRecordString(formData);
    const declaredAmount = formDataRecord.amount ? Number(formDataRecord.amount) : undefined;
    setReceiptValidation(buildReceiptAmountValidation(declaredAmount, receiptResult.total_amount));
  }, [formData, receiptResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Receipt quality gate: folded/crumpled/unreadable photos ────────────
  // When the OCR result is too weak, ask the user to retake the photo so we
  // don't submit unreadable receipts like a folded-over slip.
  useEffect(() => {
    if (!receiptResult || camera.isOpen) return;
    const key = `${receiptResult.total_amount ?? 'none'}-${receiptResult.confidence}-${(receiptResult.raw_text || '').length}`;
    if (receiptQualityKeyRef.current === key) return;
    receiptQualityKeyRef.current = key;

    const hasTotal = receiptResult.total_amount != null && receiptResult.total_amount > 0;
    const textLength = (receiptResult.raw_text || '').length;
    const confidence = receiptResult.confidence ?? 0;

    // Heuristic: low confidence + little/no extracted text means the slip was
    // likely folded, crumpled, or otherwise unreadable.
    if (confidence < 0.45 && (!hasTotal || textLength < 120)) {
      notify.confirm({
        title: 'Receipt not readable',
        message: 'This photo is too folded or unclear to read reliably. Please retake it with the full slip flat and visible.',
        confirmText: 'Retake',
        cancelText: 'Keep anyway',
        variant: 'warning',
        onConfirm: () => {
          void haptics.error();
          setReceiptPhotos((prev) => {
            const next = prev.slice(0, -1);
            if (next.length === 0) {
              setReceiptResult(null);
              setReceiptValidation(null);
              setReceiptError(null);
              setReceiptCategoryWarning(null);
              setReceiptCategorySeverity(null);
              setAiCategoryDisagreement(null);
            } else {
              // Re-analyse the remaining pages without the poor-quality one
              runReceiptAnalysis(next);
            }
            return next;
          });
          // Open the camera again so the user can immediately retake
          setTimeout(() => { void handleOpenCamera(); }, 0);
        },
        onCancel: () => {
          // User chose to keep the poor-quality photo — just mark as handled
        },
      });
    }
  }, [receiptResult, camera, handleOpenCamera, runReceiptAnalysis, setReceiptCategorySeverity, setReceiptCategoryWarning, setAiCategoryDisagreement, setReceiptError, setReceiptPhotos, setReceiptResult, setReceiptValidation]);

  // ── Amount change → re-validate ──────────────────────────────────────
  const handleAmountChange = useCallback((value: string) => {
    updateField('amount', value);
  }, [updateField]);

  // ── Travel auto-calc: amount = distance_km × rate_per_km ────────────
  useEffect(() => {
    if (selectedEntryType !== 'travel') return;
    const record = toRecordString(formData);
    const dist = parseFloat(record.distance_km);
    const rate = parseFloat(record.rate_per_km);
    if (!isNaN(dist) && !isNaN(rate) && dist > 0 && rate > 0) {
      const calculated = Math.round(dist * rate * 100) / 100;
      const currentAmount = record.amount ? parseFloat(record.amount) : NaN;
      if (isNaN(currentAmount) || Math.abs(currentAmount - calculated) > 0.01) {
        updateField('amount', calculated.toFixed(2));
      }
    }
  }, [formData, selectedEntryType, updateField]);

  // ── Back to type selection (resets all form state) ──────────────────
  const handleBackToTypeSelection = useCallback(() => {
    void haptics.light();
    setSelectedEntryType(null);
    setFormData({ type: 'purchase' });
    setReceiptPhotos([]);
    setReceiptPhotoProcessing(false);
    setReceiptResult(null);
    setReceiptValidation(null);
    setReceiptError(null);
    setReceiptCategoryWarning(null);
    setReceiptCategorySeverity(null);
    setAiCategoryDisagreement(null);
    setLinkToWorkItem(canLinkToWorkItem);
    setIsJobLinkedExpense(false);
    setLinkingMode(null);
    resetProcurement();
    resetCustomerJob();
    setExpenseLineItems([]);
    setShowAddLineItem(false);
    setNewLineItem({ description: '', quantity: 1, unit_price: 0, tax_rate_id: undefined });
    setDuplicateWarning(null);
    setDuplicateAcknowledged(false);
    setDocTypeUserPicked(false);
    setSupplierMismatchAcknowledged(false);
  }, [canLinkToWorkItem, resetProcurement, resetCustomerJob]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reset for next capture (preserves entry type + job context) ──────
  // Called when the user taps "Capture another" on the submission receipt.
  // Clears all form data, photos, and AI results but keeps the entry type,
  // job link, vehicle, and supplier context so the user can quickly capture
  // another expense for the same job.
  const resetForNextCapture = useCallback(() => {
    setSubmissionReceipt(null);
    pendingEntryRef.current = null;
    // Clear the idempotency session key so the next submit gets a fresh DIN
    // number instead of hitting the in-memory cache and returning the
    // previous submission's result.
    captureSessionResetSessionId();
    // Reset form data to initial state for the current entry type
    setFormData(createInitialFormData(selectedEntryType || 'purchase'));
    // Clear photos and AI results
    setReceiptPhotos([]);
    setReceiptPhotoProcessing(false);
    setReceiptResult(null);
    setReceiptValidation(null);
    setReceiptError(null);
    setReceiptCategoryWarning(null);
    setReceiptCategorySeverity(null);
    setAiCategoryDisagreement(null);
    // Clear line items
    setExpenseLineItems([]);
    setShowAddLineItem(false);
    setNewLineItem({ description: '', quantity: 1, unit_price: 0, tax_rate_id: undefined });
    // Clear duplicate warnings
    setDuplicateWarning(null);
    setDuplicateAcknowledged(false);
    setDocTypeUserPicked(false);
    setSupplierMismatchAcknowledged(false);
    // Reset procurement state (but keep supplier if linked)
    resetProcurement();
    // Clear persisted draft
    clearDraft();
    void haptics.light();
  }, [selectedEntryType, resetProcurement, clearDraft, captureSessionResetSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Propagate pending entry to parent (called when user taps "Done") ──
  const handleDoneFromReceipt = useCallback(() => {
    void haptics.success();
    const entry = pendingEntryRef.current;
    pendingEntryRef.current = null;
    setSubmissionReceipt(null);
    if (entry) {
      onSubmit(entry);
    }
  }, [onSubmit]);

  // Reset acknowledgment when a new duplicate check produces a *different*
  // set of results. The check can re-run when the user edits the transaction
  // date/time — if the same duplicates come back, the existing
  // acknowledgment still stands (the confirm dialog won't re-show for the
  // same ID set, so resetting it would leave the form blocked with no way
  // to acknowledge).
  const prevDupIdsRef = useRef<string | null>(null);
  useEffect(() => {
    const dupIds = duplicateWarning && duplicateWarning.length > 0
      ? duplicateWarning.map(d => d.id).sort().join(',')
      : null;
    if (dupIds && dupIds !== prevDupIdsRef.current) {
      setDuplicateAcknowledged(false);
    }
    prevDupIdsRef.current = dupIds;
  }, [duplicateWarning]);

  // Show notify.confirm dialog when duplicates are detected
  // Track the last shown duplicate IDs to avoid re-showing the same dialog
  const lastShownDupIdsRef = useRef<string | null>(null);
  useEffect(() => {
    if (!duplicateWarning || duplicateWarning.length === 0) {
      lastShownDupIdsRef.current = null;
      return;
    }
    const dupIds = duplicateWarning.map(d => d.id).sort().join(',');
    if (lastShownDupIdsRef.current === dupIds) return; // Already shown for this set
    lastShownDupIdsRef.current = dupIds;

    logger.debug('[ChildEntryForm] Duplicates detected, showing notify.confirm:', duplicateWarning);
    const dupList = duplicateWarning.map(d =>
      `• ${d.item_number || d.title} — R${Number(d.grand_total).toFixed(2)}${d.expense_date ? ` on ${d.expense_date}` : ''} (${d.match_confidence} match, ${d.status})`
    ).join('\n');
    notify.confirm({
      title: 'Possible Duplicate Expense Detected',
      message: `This receipt may have already been submitted.\n\n${dupList}\n\nDo you want to proceed with saving this expense?`,
      confirmText: 'Proceed with Save',
      cancelText: 'Cancel Entry',
      variant: 'warning',
      onConfirm: () => {
        void haptics.warning();
        setDuplicateAcknowledged(true);
      },
      onCancel: () => {
        void haptics.error();
        setDuplicateWarning(null);
        setDuplicateAcknowledged(false);
        lastShownDupIdsRef.current = null;
        onCancel();
      },
    });
  }, [duplicateWarning]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show notify.confirm dialog when supplier name mismatch is detected
  const lastMismatchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!supplierMismatch) {
      lastMismatchKeyRef.current = null;
      return;
    }
    const key = `${selectedSupplierName}|${aiVendorName}`;
    if (lastMismatchKeyRef.current === key) return;
    lastMismatchKeyRef.current = key;

    notify.confirm({
      title: 'Supplier Name Mismatch',
      message: `You selected "${selectedSupplierName}" but the AI detected "${aiVendorName}" on the document.\n\nDo you want to proceed with saving this expense under "${selectedSupplierName}"?`,
      confirmText: 'Submit anyway',
      cancelText: 'Cancel',
      variant: 'warning',
      onConfirm: () => {
        void haptics.warning();
        setSupplierMismatchAcknowledged(true);
      },
      onCancel: () => {
        void haptics.error();
        setSupplierMismatchAcknowledged(false);
      },
    });
  }, [supplierMismatch, selectedSupplierName, aiVendorName]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save draft on page close / app background ────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = () => saveDraft();
    const visHandler = () => {
      if (document.visibilityState === 'hidden') saveDraft();
    };
    window.addEventListener('beforeunload', handler);
    document.addEventListener('visibilitychange', visHandler);
    return () => {
      window.removeEventListener('beforeunload', handler);
      document.removeEventListener('visibilitychange', visHandler);
      saveDraft();
    };
  }, [isOpen, saveDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Guard: after all hooks ───────────────────────────────────────────
  if (!isOpen) return null;

  // Show type selection screen if no entry type is selected
  if (!selectedEntryType) {
    return (
      <TypeSelectionScreen
        isMobile={isMobile}
        isDark={isDark}
        procurementHiddenTypes={procurementHiddenTypes}
        features={features}
        onCancel={onCancel}
        onBackToBeginAction={onBackToBeginAction}
        onSelect={(type, isJobLinked) => {
          void haptics.selection();
          setSelectedEntryType(type);
          setFormData(createInitialFormData(type));
          if (type === 'purchase' && isJobLinked !== undefined) {
            setIsJobLinkedExpense(isJobLinked);
            setLinkToWorkItem(isJobLinked && canLinkToWorkItem);
          } else {
            setIsJobLinkedExpense(false);
            setLinkToWorkItem(canLinkToWorkItem);
          }
        }}
      />
    );
  }

  if (!config) return null;

  const Icon = config.icon;

  const formDataRecord = toRecordString(formData);
  const fieldValueComplete = (f: { key: string; type?: string }): boolean => {
    const v = formDataRecord[f.key];
    if (!v || String(v).trim() === '') return false;
    // Datetime fields require a complete date + time. A date-only value
    // means the AI found no time on the slip — the user must pick one so
    // the transaction time is a reliable duplicate-detection signal.
    if (f.type === 'datetime') return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(String(v));
    return true;
  };
  const requiredComplete = config.fields
    .filter((f) => f.required || (needsOdometer && f.key === 'odometer'))
    .every(fieldValueComplete);

  const missingRequiredFields = config.fields
    .filter((f) => (f.required || (needsOdometer && f.key === 'odometer')) && !fieldValueComplete(f))
    .map((f) => f.label);

  // ── Submit handler (delegates to useCaptureSession) ─────────────────
  const handleSubmit = async () => {
    void haptics.medium();
    const odomResult = needsOdometer ? odometerCapture.getResult() : null;
    const formDataRecord = toRecordString(formData);

    // §3.2.3 — Supplier invoice duplicate check on form submit.
    // Calls detect-supplier-invoice-duplicate semantic action with
    // (contact_id, invoice_number, amount) and warns if a duplicate is found.
    if (selectedEntryType === 'supplier_invoice' && selectedSupplier?.id) {
      const invoiceNumber = formDataRecord.invoice_number || formDataRecord.receipt_number || '';
      const amount = Number(formDataRecord.amount || 0);
      if (invoiceNumber && amount > 0) {
        try {
          const token = authService.getTokens()?.access_token;
          const dupRes = await fetch('/api/v1/finance/semantic-intake', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token || ''}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action: 'detect-supplier-invoice-duplicate',
              contact_id: Number(selectedSupplier.id),
              invoice_number: invoiceNumber,
              total_amount: amount,
            }),
          });
          if (dupRes.ok) {
            const dupData = await dupRes.json();
            const duplicates = dupData?.data?.duplicates ?? dupData?.duplicates ?? [];
            if (Array.isArray(duplicates) && duplicates.length > 0) {
              const proceed = (await confirm({ message: `Possible duplicate detected: ${duplicates.length} existing supplier invoice(s) match "${invoiceNumber}" for this supplier. Submit anyway?`, variant: 'warning' }));
              if (!proceed) return;
            }
          }
        } catch {
          // Non-blocking — if the duplicate check fails, proceed with submit
        }
      }
    }

    await captureSessionSubmit({
      selectedEntryType,
      workItemId,
      linkToWorkItem,
      linkableWorkItemId,
      selectedJob,
      selectedCustomer,
      linkingMode,
      selectedSupplier,
      selectedPurchaseOrder,
      linkToSupplier,
      linkToVehicle,
      vehicleId: effectiveVehicleId,
      vehicleRegistration: effectiveVehicleRegistration,
      gpsLocation,
      workflowMode,
      requiresFinanceApproval,
      receiptCategorySeverity,
      aiCategoryDisagreement,
      needsOdometer,
      odometerResult: odomResult,
      isCasualWage,
      isProcurementType,
      requiresPurchaseOrder,
      poFulfillment,
      poLineItems,
      supplierMismatchAcknowledged,
      paymentTermsReverted,
      aiVendorName: aiVendorName || null,
      expenseLineItems,
      expenseAccounts,
      expenseAccountMappings,
      policies,
      clientId: realtimeClientId,
      onSubmit: (entry) => {
        // Store entry for later propagation via "Done" button
        pendingEntryRef.current = entry;
        // Show submission receipt with capture-another / done buttons
        setSubmissionReceipt({
          entryType: entry.type,
          amount: entry.amount,
          currency: detectedCurrency,
          vendor: formDataRecord.vendor || formDataRecord.supplier_name || receiptResult?.vendor_name || undefined,
          expenseDate: formDataRecord.date || new Date().toISOString().split('T')[0],
          photoCount: entry.photos?.length || 0,
          receiptAiValidated: !!receiptResult,
          requiresApproval: !!requiresFinanceApproval,
          workflowMode: workflowMode || undefined,
          intakeId: (entry.metadata?.expense_intake_id as string | number | undefined),
          itemNumber: (entry.metadata?.item_number as string | undefined),
        });
      },
    });

    // Clear persisted draft on successful submission
    clearDraft();
    void haptics.success();
  };

  // ── Gemini AI receipt analysis overlay ───────────────────────────────
  if (receiptValidating && !submitting && !camera.isOpen) {
    return <AiAnalysisOverlay accent={accent} />;
  }

  // ── Splash / loading overlay ─────────────────────────────────────────
  if (submitting) {
    return <SubmittingOverlay loadingPhase={loadingPhase} accent={accent} />;
  }

  // ── Full-screen panel ────────────────────────────────────────────────
  const panelFrameClassName = `fixed flex flex-col bg-white dark:bg-gray-900 ${getSmartTaskViewportPageFrameClassName({
    isTabletPortal,
    isMobile,
  })
    }`;
  const panelFrameStyle = {
    ...getSmartTaskViewportPageFrameStyle({
      isTabletPortal,
      isMobile,
      headerHidden,
    }),
    zIndex: Math.max(SMARTTASK_TOPMOST_FULLSCREEN_MODAL_Z_INDEX, CHILD_ENTRY_FORM_Z_INDEX),
  };

  const modalContent = (
    <>
      <div
        data-tour-id="smarttask-child-entry-form"
        className={panelFrameClassName}
        style={panelFrameStyle}
      >
        {/* ── Sticky Header ─────────────────────────────────────── */}
        <EntryFormHeader
          config={config}
          selectedEntryType={selectedEntryType}
          entryTypeProp={entryTypeProp}
          titleOverride={titleOverride}
          isJobLinkedExpense={isJobLinkedExpense}
          submitting={submitting}
          onBackToBeginAction={onBackToBeginAction}
          onCancel={onCancel}
          onBackToTypeSelection={handleBackToTypeSelection}
          hideCloseButton={hideCloseButton}
        />

        {/* ── Scrollable Content ──────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-3 pb-6">
          <div className="max-w-4xl mx-auto space-y-3 pt-3">

            {/* ── Forced linking mode denied by RBAC (expenses-management
                matrix row unchecked for the user's role scope) ── */}
            {forcedLinkingMode && !allowedLinkingModes.has(forcedLinkingMode) && (
              <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3">
                <p className="text-xs text-amber-700 dark:text-amber-300 text-center">
                  You do not have permission for this capture mode. Contact your administrator.
                </p>
              </div>
            )}

            {/* ── Collapsible Selection Sections (hidden after photo capture) ── */}
            {/* Once a photo is captured, the linking/category selection is locked.
                The accordion is completely hidden — not even collapsed — to
                prevent users from changing the category after capture. */}
            {receiptPhotos.length === 0 && (
            <SelectionSectionsAccordion
              selectionExpanded={selectionExpanded}
              setSelectionExpanded={setSelectionExpanded}
              showVehicleLinkToggle={showVehicleLinkToggle}
              isSmartCaptureLinkingMode={isSmartCaptureLinkingMode}
              hideLinkingModeSelector={!!forcedLinkingMode}
              showLinkToggle={showLinkToggle}
              canSwitchEntryTypeInCamera={effectiveCapabilities.canSwitchEntryTypeInCamera}
              isProcurementType={isProcurementType}
              linkToWorkItem={linkToWorkItem}
              isFormVisible={isFormVisible}
              hasVehicleAssigned={!!vehicleId}
              effectiveVehicleRegistration={effectiveVehicleRegistration}
              linkToVehicle={linkToVehicle}
              vehicleRegistration={vehicleRegistration}
              linkingMode={linkingMode}
              vehicleSubType={vehicleSubType}
              selectedSupplier={selectedSupplier}
              linkableWorkItemNumber={linkableWorkItemNumber}
              isJobLinkedExpense={isJobLinkedExpense}
              selectedCustomer={selectedCustomer}
              selectedJob={selectedJob}
              vehicleToggleProps={{
                showVehicleLinkToggle,
                linkToVehicle,
                setLinkToVehicle,
                vehicleId,
                vehicleRegistration,
                searchedVehicle,
                setSearchedVehicle,
                vehicleSearchQuery,
                setVehicleSearchQuery,
                showVehicleSearch,
                setShowVehicleSearch,
                loadingVehicles,
                filteredVehicles,
                vehicleDropdownRef,
              }}
              linkingModeProps={{
                linkingMode,
                setLinkingMode: (mode) => { if (mode !== null) void haptics.toggle(); setLinkingMode(mode); },
                isDark,
                hasVehicleAssigned: !!vehicleId,
                vehicleSubType,
                setVehicleSubType: (sub) => { if (sub !== null) void haptics.toggle(); setVehicleSubType(sub); },
                allowedModes: allowedLinkingModes,
              }}
              supplierDropdownProps={{
                selectedSupplier,
                setSelectedSupplier,
                supplierSearchQuery,
                setSupplierSearchQuery,
                showSupplierSearch,
                setShowSupplierSearch,
                filteredSuppliers,
                loadingSuppliers,
                supplierDropdownRef,
              }}
              appointmentToggleProps={{
                showLinkToggle: !!showLinkToggle,
                canLinkToWorkItem,
                linkToWorkItem,
                setLinkToWorkItem,
                isProcurementType,
                isDark,
                requiresPurchaseOrder,
                linkableWorkItemId,
                linkableWorkItemHasLinkedPOs,
                linkableWorkItemNumber,
                linkableWorkItemLocation,
                linkableWorkItemContactName,
                linkableWorkItemTitle,
                linkableWorkItemLabel,
                isJobLinkedExpense,
                selectedCustomer,
                setSelectedCustomer,
                customerSearchQuery,
                setCustomerSearchQuery,
                showCustomerSearch,
                setShowCustomerSearch,
                loadingCustomers,
                filteredCustomers,
                customerDropdownRef,
                selectedJob,
                setSelectedJob,
                jobSearchQuery,
                setJobSearchQuery,
                showJobSearch,
                setShowJobSearch,
                loadingJobs,
                jobsChecked,
                filteredJobs,
                activeJobs,
                jobDropdownRef,
              }}
              procurementPreCaptureProps={{
                isProcurementType,
                linkToWorkItem,
                isFormVisible,
                requiresPurchaseOrder,
                selectedSupplier,
                setSelectedSupplier,
                supplierSearchQuery,
                setSupplierSearchQuery,
                showSupplierSearch,
                setShowSupplierSearch,
                filteredSuppliers,
                loadingSuppliers,
                selectedPurchaseOrder,
                setSelectedPurchaseOrder,
                poSearchQuery,
                setPoSearchQuery,
                poSearchResults,
                isSearchingPO,
                showPOSearch,
                setShowPOSearch,
                procurementSetupExpanded,
                setProcurementSetupExpanded,
                poLineItems,
                poFulfillment,
                supplierDropdownRef,
                poDropdownRef,
                selectedEntryType,
                onClearPO: () => {
                  setSelectedPurchaseOrder(null);
                  setPoSearchQuery('');
                },
              }}
              lockToVehicleExpense={lockToVehicleExpense}
            />
            )}

            {/* ── AI Auto-Switch Banner (non-blocking, allows user correction) ── */}
            {/* Rendered outside isFormVisible so it shows even when procurement prerequisites aren't met yet */}
            {autoSwitchBanner && (
              <AutoSwitchBanner
                banner={autoSwitchBanner}
                onRevert={(originalType) => {
                  autoSwitchingRef.current = true;
                  setSelectedEntryType(originalType);
                  dismissAutoSwitchBanner();
                }}
                onDismiss={dismissAutoSwitchBanner}
              />
            )}

            {/* ── Receipt Photos, Delivery Note, Form Fields (hidden until prerequisites met) ── */}
            {isFormVisible && (
                <>
                  {/* ── Fuel Insights Card (fuel entries — shown before capture too) ── */}
                  {/* Shows vehicle tracking status, auto-fetched odometer, and
                      previous fill-up context so the user sees mileage info
                      immediately, even before taking a receipt photo. */}
                  {isFuelEntry && (
                    <FuelInsightsCard
                      lastFuelEntry={lastFuelEntry}
                      loadingLastFuelEntry={loadingLastFuelEntry}
                      currentOdometer={formDataRecord.odometer ? Number(formDataRecord.odometer) : null}
                      currentLitres={formDataRecord.litres ? Number(formDataRecord.litres) : null}
                      currentAmount={formDataRecord.amount ? Number(formDataRecord.amount) : null}
                      detectedCurrency={detectedCurrency}
                      vehicleInfo={vehicleInfo}
                      vehicleRegistration={effectiveVehicleRegistration ?? null}
                      isDark={isDark}
                      accent={accent}
                    />
                  )}

                  {/* ── Receipt Photos (expense types only, except travel which uses logbook data) ── */}
                  {requiresReceiptPhoto && (
                    <ReceiptCaptureSection
                      receiptPhotos={receiptPhotos}
                      receiptPhotoProcessing={receiptPhotoProcessing}
                      receiptValidating={receiptValidating}
                      receiptResult={receiptResult}
                      receiptValidation={receiptValidation}
                      receiptError={receiptError}
                      receiptCategoryWarning={receiptCategoryWarning}
                      receiptCategorySeverity={receiptCategorySeverity}
                      captureProfile={captureProfile}
                      setCaptureProfile={() => {}}
                      isCasualWage={isCasualWage}
                      isOnline={isOnline}
                      accent={accent}
                      onOpenCamera={handleOpenCamera}
                      onUploadPdf={importPdf}
                      isImportingPdf={isImportingPdf}
                      importErrorPdf={importErrorPdf}
                      importProgressPdf={importProgressPdf}
                      onUploadImages={importImages}
                      isImportingImages={isImportingImages}
                      importErrorImages={importErrorImages}
                      onRemovePhoto={(idx) => {
                        void haptics.light();
                        setReceiptPhotos(prev => {
                          const next = prev.filter((_, i) => i !== idx);
                          if (next.length === 0) {
                            setReceiptResult(null);
                            setReceiptValidation(null);
                            setReceiptError(null);
                          }
                          return next;
                        });
                      }}
                      captureGuide={captureGuide}
                      selectedCaptureProfile={{ label: 'Auto', helper: 'Let AI detect the layout' }}
                      vehicleRegistration={isVehicleLinkedExpenseType(selectedEntryType) ? effectiveVehicleRegistration : null}
                    />
                  )}

                  {/* ── Post-capture content — hidden until at least one photo is taken ── */}
                  {receiptPhotos.length > 0 && (<>

                  <ProcurementPostCapture
                    isProcurementType={isProcurementType}
                    linkToWorkItem={linkToWorkItem}
                    aiSuggestingSupplier={aiSuggestingSupplier}
                    aiSupplierMatch={aiSupplierMatch}
                    selectedSupplier={selectedSupplier}
                    setSelectedSupplier={setSelectedSupplier}
                    supplierSearchQuery={supplierSearchQuery}
                    setSupplierSearchQuery={setSupplierSearchQuery}
                    showSupplierSearch={showSupplierSearch}
                    setShowSupplierSearch={setShowSupplierSearch}
                    filteredSuppliers={filteredSuppliers}
                    loadingSuppliers={loadingSuppliers}
                    selectedPurchaseOrder={selectedPurchaseOrder}
                    setSelectedPurchaseOrder={setSelectedPurchaseOrder}
                    poSearchQuery={poSearchQuery}
                    setPoSearchQuery={setPoSearchQuery}
                    poSearchResults={poSearchResults}
                    isSearchingPO={isSearchingPO}
                    showPOSearch={showPOSearch}
                    setShowPOSearch={setShowPOSearch}
                    procurementSetupExpanded={procurementSetupExpanded}
                    setProcurementSetupExpanded={setProcurementSetupExpanded}
                    requiresPurchaseOrder={requiresPurchaseOrder}
                    poLineItems={poLineItems}
                    poFulfillment={poFulfillment}
                    supplierDropdownRef={supplierDropdownRef}
                    poDropdownRef={poDropdownRef}
                    selectedEntryType={selectedEntryType}
                    onClearPO={() => {
                      setSelectedPurchaseOrder(null);
                      setPoSearchQuery('');
                    }}
                  />

                  {/* ── Payment-Terms Auto-Switch Notice ────────────────────────── */}
                  {paymentTermsSwitchFrom && selectedSupplier && supplierContext && (
                    <div className="mt-2 flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-900/40 dark:bg-amber-950/20">
                      <div className="text-[11px] text-amber-800 dark:text-amber-300">
                        Switched to <strong>Supplier Invoice</strong> because {supplierContext.paymentTermsDays ?? 0}-day payment terms were detected for this supplier.
                      </div>
                      <button
                        type="button"
                        className="ml-2 shrink-0 rounded border border-amber-400 px-2 py-0.5 text-[10px] font-medium text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-300 dark:hover:bg-amber-900/30"
                        onClick={() => {
                          autoSwitchingRef.current = true;
                          setSelectedEntryType(paymentTermsSwitchFrom);
                          setPaymentTermsSwitchFrom(null);
                          setPaymentTermsReverted(true);
                        }}
                      >
                        Revert to Expense
                      </button>
                    </div>
                  )}

                  {/* ── Supplier Context Enrichment (§3.2.3) ─────────────────── */}
                  {selectedSupplier && supplierContext && (
                    <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50/50 p-3 dark:border-blue-900/30 dark:bg-blue-950/20">
                      <p className="mb-1.5 text-xs font-semibold text-blue-700 dark:text-blue-300">
                        Supplier Context
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-[11px]">
                        {supplierContext.outstandingBalance != null && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Outstanding:</span>{' '}
                            <span className="font-medium">R {supplierContext.outstandingBalance.toFixed(2)}</span>
                          </div>
                        )}
                        {supplierContext.paymentTermsDays != null && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Payment terms:</span>{' '}
                            <span className="font-medium">{supplierContext.paymentTermsDays} days</span>
                          </div>
                        )}
                        {supplierContext.creditLimit != null && supplierContext.creditLimit > 0 && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Credit limit:</span>{' '}
                            <span className="font-medium">R {supplierContext.creditLimit.toFixed(2)}</span>
                          </div>
                        )}
                        {supplierContext.creditLimit != null && supplierContext.creditLimit > 0 &&
                          supplierContext.currentBalance != null && Number(formDataRecord?.amount || 0) > 0 &&
                          supplierContext.currentBalance + Number(formDataRecord?.amount || 0) > supplierContext.creditLimit && (
                          <div className="col-span-2 mt-1 rounded bg-amber-100 px-2 py-1 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                            ⚠ Credit limit warning: this invoice may exceed the supplier's credit limit.
                          </div>
                        )}
                      </div>
                      {supplierContext.recentDocuments.length > 0 && (
                        <div className="mt-2">
                          <p className="mb-1 text-[10px] font-medium text-gray-500 dark:text-gray-400">Recent documents:</p>
                          <div className="space-y-0.5">
                            {supplierContext.recentDocuments.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between text-[10px] text-gray-600 dark:text-gray-400">
                                <span>{doc.item_number}</span>
                                <span className="font-medium">R {doc.grand_total.toFixed(2)}</span>
                                <span className="text-gray-400">{doc.status}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {loadingSupplierContext && (
                        <p className="text-[10px] text-gray-400 mt-1">Loading supplier context…</p>
                      )}
                    </div>
                  )}

                  {/* ── OCR Confidence Indicator ─────────────────────────────── */}
                  {receiptResult && (
                    <OcrConfidenceIndicator receiptResult={receiptResult} />
                  )}

                  <FormFieldsSection
                    selectedEntryType={selectedEntryType}
                    config={config}
                    formData={formDataRecord}
                    updateField={updateField}
                    handleAmountChange={handleAmountChange}
                    isExpenseType={isExpenseType}
                    isDark={isDark}
                    detectedCurrency={detectedCurrency}
                    needsOdometer={!!needsOdometer}
                    odometerCapture={odometerCapture}
                    submitting={submitting}
                    accent={accent}
                    vehicleInfo={vehicleInfo}
                    expenseLineItems={expenseLineItems}
                    setExpenseLineItems={setExpenseLineItems}
                    showAddLineItem={showAddLineItem}
                    setShowAddLineItem={setShowAddLineItem}
                    newLineItem={newLineItem}
                    setNewLineItem={setNewLineItem}
                    taxRates={taxRates}
                    preferredDefaultTaxRate={preferredDefaultTaxRate ?? null}
                    expenseAccounts={expenseAccounts}
                    capabilities={effectiveCapabilities}
                    poFulfillment={poFulfillment}
                    displayAccountCode={displayAccountCode}
                    isJobLinkedExpense={isJobLinkedExpense}
                    lineItemDuplicates={lineItemDuplicates}
                    onRemoveDuplicateLineItems={removeDuplicateLineItems}
                    onDismissLineItemDuplicates={dismissLineItemDuplicates}
                  />
                  </>
                )}
                </>
              )}
          </div>
        </div>

        {/* ── Bottom Action Bar ────────────────────────────────── */}
        <BottomActionBar
          isFormVisible={isFormVisible}
          receiptPhotos={receiptPhotos}
          submitting={submitting}
          receiptPhotoProcessing={receiptPhotoProcessing}
          isCasualWage={isCasualWage}
          receiptValidating={receiptValidating}
          gpsLocation={gpsLocation}
          isDark={isDark}
          selectedEntryType={selectedEntryType}
          poFulfillment={poFulfillment}
          missingRequiredFields={missingRequiredFields}
          requiredComplete={requiredComplete}
          receiptCategorySeverity={receiptCategorySeverity}
          hasUnacknowledgedDuplicates={hasUnacknowledgedDuplicates}
          pendingDocTypeDetermination={pendingDocTypeDetermination}
          onPickDocType={(type) => {
            setDocTypeUserPicked(true);
            setSelectedEntryType(type);
            void haptics.medium();
          }}
          hasUnacknowledgedSupplierMismatch={hasUnacknowledgedSupplierMismatch}
          accent={accent}
          onSubmit={handleSubmit}
        />
      </div>

      {/* ── Submission Receipt (success overlay) ─────────────── */}
      {submissionReceipt && (
        <SubmissionReceipt
          data={submissionReceipt}
          onDismiss={() => {
            setSubmissionReceipt(null);
          }}
          // "Capture another" is only useful for multi-photo long till slips.
          // Fuel and toll slips always fit on a single photo, so hide the
          // button and only show "Done".
          onCaptureAnother={
            submissionReceipt.entryType === 'fuel' || submissionReceipt.entryType === 'toll'
              ? undefined
              : resetForNextCapture
          }
          onDone={handleDoneFromReceipt}
        />
      )}

      {/* ── Capture Button Config Selection (SmartAutomation) ── */}
      {configSlotIndex !== null && (
        <CaptureButtonConfigModal
          slotIndex={configSlotIndex}
          configurableEntryTypes={CONFIGURABLE_ENTRY_TYPES}
          onSelect={handleConfigSelect}
          onClose={() => setConfigSlotIndex(null)}
        />
      )}

      {/* ── Camera Modal (renders at z-[160] above panel) ──── */}
      {/* Build expense categorization text for the smart review screen.
          This is a final recap so the user can confirm they're submitting
          the right slip for the right expense. */}
      {(() => {
        const supplierName = selectedSupplier?.company_name || selectedSupplier?.name || '';
        const jobNumber = selectedJob?.item_number || linkableWorkItemNumber || '';
        const jobCustomer = selectedCustomer?.company_name || selectedCustomer?.contact_person || linkableWorkItemContactName || '';
        const vehicleReg = effectiveVehicleRegistration || '';
        const amount = formDataRecord.amount || '';
        const expenseDate = formDataRecord.date || '';
        const vendorName = formDataRecord.vendor || formDataRecord.supplier_name || '';

        let ctx = '';
        if (isCasualWage) {
          // Casual wages - R600 24Aug2026 /n Job - JB1234
          const wagePart = [amount && `R${amount}`, expenseDate].filter(Boolean).join(' ');
          const jobPart = jobNumber && `Job - ${jobNumber}${jobCustomer ? ' ' + jobCustomer : ''}`;
          ctx = ['Casual wages', wagePart && `- ${wagePart}`, jobPart].filter(Boolean).join(' \n ');
        } else if (linkingMode === 'vehicle' || isVehicleLinkedExpenseType(selectedEntryType)) {
          // Vehicle expense - Fuel MF23GHGP /n Total Northrand Boksburg
          const subTypeLabel = selectedEntryType === 'fuel' ? 'Fuel'
            : selectedEntryType === 'toll' ? 'Toll'
            : selectedEntryType === 'maintenance' ? 'Maintenance'
            : 'Vehicle';
          ctx = `Vehicle expense - ${subTypeLabel} ${vehicleReg}` + (vendorName ? ` \n ${vendorName}` : '');
        } else if (linkingMode === 'both') {
          // Supplier - Delivery Note /n Job - JB1234 City Properties (Pty) Ltd
          const entryLabel = config?.label || 'Supplier + Job';
          ctx = `${entryLabel} - ${supplierName}` + (jobNumber ? ` \n Job - ${jobNumber}${jobCustomer ? ' ' + jobCustomer : ''}` : '');
        } else if (linkingMode === 'supplier' || (isProcurementType && supplierName)) {
          // Supplier Invoice - Plumblink
          const entryLabel = config?.label || 'Supplier';
          ctx = `${entryLabel} - ${supplierName}` + (vendorName && vendorName !== supplierName ? ` \n ${vendorName}` : '');
        } else if (linkingMode === 'job' || isJobLinkedExpense) {
          // Job linked - JB1234 /n City Properties (Pty) Ltd
          ctx = `Job linked - ${jobNumber}` + (jobCustomer ? ` \n ${jobCustomer}` : '');
        } else if (linkingMode === 'general' || selectedEntryType === 'purchase') {
          // General expense /n Adendorff 26Aug2026
          const detail = [vendorName, expenseDate].filter(Boolean).join(' ');
          ctx = 'General expense' + (detail ? ` \n ${detail}` : '');
        } else if (config?.label) {
          ctx = config.label + (vendorName ? ` \n ${vendorName}` : '') + (expenseDate ? ` ${expenseDate}` : '');
        }

        return (
      <CameraModal
        camera={camera}
        title={cameraConfig.camera_title || 'Receipt Photo'}
        guide={{
          mode: captureProfile,
          title: cameraConfig.guide_title || captureGuide.title,
          subtitle: cameraConfig.guide_subtitle || captureGuide.subtitle,
        }
        }
        cropToGuide
        autoCapture={cameraConfig.auto_capture ?? true}
        autoZoom={cameraConfig.auto_zoom ?? true}
        reviewCapture
        batchCapture={cameraConfig.batch_capture ?? true}
        reviewButtons={cameraConfig.review_buttons || 'captureAnotherDone'}
        shortcuts={[]}
        captureModeGrid={captureModeButtons.length > 0 ? captureModeButtons : undefined}
        perspectiveCorrection={cameraConfig.perspective_correction ?? true}
        darkModeEnhance={cameraConfig.dark_mode_enhance ?? true}
        contextPhotos={cameraConfig.show_context_photos ? receiptPhotos : []}
        captureMode={cameraConfig.batch_capture && effectiveCapabilities.canSwitchEntryTypeInCamera ? 'combine' : undefined}
        expenseContext={cameraConfig.show_context_photos && ctx ? ctx : undefined}
        documentProfile={cameraConfig.document_profile || 'auto'}
        requireReadableDocument={cameraConfig.require_readable ?? true}
      />
        );
      })()}

      <GuidedTour
        moduleKey="total-operations"
        activeTab={selectedEntryType ?? 'purchase'}
        onNavigate={() => {}}
        embeddedInModal
        autoFlowId="smarttask-child-entry-form"
      />
    </>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent;
}
